import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { UsageStatsResponse, SubscriptionDetails, UsageEvent } from '@nvgc/shared';

interface UsageDashboardProps {
  onUpgrade?: () => void;
}

interface UsageHistory {
  events: UsageEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const UsageDashboard: React.FC<UsageDashboardProps> = ({ onUpgrade }) => {
  const [usageStats, setUsageStats] = useState<UsageStatsResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    loadUsageData();
  }, []);

  useEffect(() => {
    if (usageStats) {
      loadUsageHistory(historyPage);
    }
  }, [historyPage, usageStats]);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load usage stats and subscription in parallel
      const [statsResponse, subscriptionResponse] = await Promise.all([
        fetch('/api/usage/stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }),
        fetch('/api/billing/subscription', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        })
      ]);

      if (!statsResponse.ok) {
        throw new Error('Failed to load usage statistics');
      }

      if (!subscriptionResponse.ok) {
        throw new Error('Failed to load subscription details');
      }

      const statsData = await statsResponse.json();
      const subscriptionData = await subscriptionResponse.json();

      setUsageStats(statsData.data);
      setSubscription(subscriptionData.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageHistory = async (page: number) => {
    try {
      const response = await fetch(`/api/usage/history?page=${page}&pageSize=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load usage history');
      }

      const data = await response.json();
      setUsageHistory(data);
    } catch (err) {
      console.error('Failed to load usage history:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  const formatEventType = (eventType: string) => {
    switch (eventType) {
      case 'chat_query':
        return 'Chat Query';
      case 'document_upload':
        return 'Document Upload';
      default:
        return eventType;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadUsageData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!usageStats || !subscription) {
    return (
      <Alert severity="info">
        No usage data available
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Usage Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Current Usage Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Current Usage
                </Typography>
              </Box>
              
              <Typography variant="h3" color="primary" gutterBottom>
                {usageStats.currentPeriodUsage.toLocaleString()}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                of {usageStats.currentPeriodLimit.toLocaleString()} queries
              </Typography>
              
              <LinearProgress
                variant="determinate"
                value={usageStats.usagePercentage}
                color={getUsageColor(usageStats.usagePercentage)}
                sx={{ mt: 2, mb: 1 }}
              />
              
              <Typography variant="body2" color="text.secondary">
                {usageStats.usagePercentage}% used
              </Typography>

              {usageStats.usagePercentage >= 90 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  You're approaching your query limit. Consider upgrading your plan.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Subscription
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h5" sx={{ textTransform: 'capitalize' }}>
                  {subscription.planType}
                </Typography>
                <Chip 
                  label={subscription.status}
                  color={getStatusColor(subscription.status) as any}
                  size="small"
                />
              </Box>
              
              {subscription.currentPeriodEnd && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Renews on {format(subscription.currentPeriodEnd, 'MMM dd, yyyy')}
                </Typography>
              )}

              {subscription.cancelAtPeriodEnd && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <WarningIcon sx={{ mr: 1 }} />
                  Your subscription will cancel at the end of the current period.
                </Alert>
              )}

              <Box mt={2}>
                <Button
                  variant="outlined"
                  onClick={onUpgrade}
                  fullWidth
                >
                  Manage Subscription
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage History
              </Typography>
              
              {usageHistory && usageHistory.events.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Tokens Used</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageHistory.events.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              {format(event.createdAt, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={formatEventType(event.eventType)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {event.tokensUsed.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {usageHistory.totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Pagination
                        count={usageHistory.totalPages}
                        page={historyPage}
                        onChange={(_, page) => setHistoryPage(page)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No usage history available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};